/* istanbul ignore file */

import {
  extendTheme,
  withDefaultSize,
  withDefaultColorScheme,
} from "@chakra-ui/react";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/700.css";

import { Button, Divider, Heading, Skeleton, Input, Tabs } from "./components";
import { generateIntegratorColorScale } from "./utils";
export { PublisherThemeConfigProvider } from "./PublisherThemeConfig";
export { default as PublisherThemeExtendedChakraProvider } from "./PublisherThemeExtendedChakra";

const tiliaPrimaryColorScale = generateIntegratorColorScale("#004D99"); // Tilia's Royal Blue

/**
 * baseTheme
 * @description
 * Sets some defaults for our Chakra theme. The @type above is used in order to
 * get some of the IDE benefits of TS without moving this project over to TS.
 *
 * Note that since we're dealing with our publishers being able to provide us with any color
 * they want as a background color, we have our foreground colors set as rgba's and rely on
 * opacity to push elements back. This seems to be the best way to deal with the broad range
 * of colors we can end up with.
 */
const baseTheme = {
  // More info here: https://chakra-ui.com/community/recipes/using-fonts
  fonts: {
    body: `'Roboto',sans-serif`,
  },

  colors: {
    black: "#1B2228",
    white: "#ffffff",
    integrator: tiliaPrimaryColorScale,
  },

  // For more information on semantic tokens, see: https://chakra-ui.com/docs/features/semantic-tokens
  semanticTokens: {
    colors: {
      primaryText: {
        default: "black",
        _dark: "white",
      },
      secondaryText: {
        default: "rgba(27, 34, 40, 0.75)", // Note that this rgb value is the same as our 'black' color's hex value above
        _dark: "rgba(255, 255, 255, 0.75)",
      },
      tertiaryText: {
        default: "rgba(27, 34, 40, 0.45)",
        _dark: "rgba(255, 255, 255, 0.45)",
      },
      successText: {
        default: "green.600",
        _dark: "green.300",
      },
      errorText: {
        default: "red.600",
        _dark: "red.300",
      },
      background: {
        default: "white",
        _dark: "gray.800",
      },
      lightBackground: {
        default: `${tiliaPrimaryColorScale[50]}80`,
        _dark: `${tiliaPrimaryColorScale[50]}33`,
      },
      lighterBackground: {
        default: `${tiliaPrimaryColorScale[50]}40`,
        _dark: `${tiliaPrimaryColorScale[50]}10`,
      },
      lightBorder: {
        default: `${tiliaPrimaryColorScale[100]}66`,
        _dark: `${tiliaPrimaryColorScale[100]}26`,
      },
    },
  },

  styles: {
    global: ({ colorMode }) => ({
      "*": {
        // This is how Chakra styles their dividers ... not entirely sure why but ¯\_(ツ)_/¯
        borderColor: colorMode === "dark" ? "rgba(255, 255, 255, 0.1) " : "rgba(27, 34, 40, 0.1) ",
      },
      body: {
        color: colorMode === "dark" ? "white" : "black",
        bg: "background",
      },
    }),
  },

  components: {
    Button,
    Divider,
    Heading,
    Skeleton,
    Input,
    Tabs,
    Checkbox: {
      baseStyle: ({ colorMode }) => ({
        control: {
          borderColor: colorMode === "dark" ? "rgba(255, 255, 255, 0.6)" : "rgba(27, 34, 40, 0.3)"
        }
      }),
    },
  },

  config: {
    cssVarPrefix: "tilia",
    useSystemColorMode: false,
  },
};

/**
 * withPublisherThemeConfig
 * @description
 * Overrides our base theme with a theme config provided by a publisher
 */
export const withPublisherThemeConfig = (config) => {
  if (!config) {
    return {};
  }

  let out = {};

  if (config.backgroundColor) {
    out = {
      styles: {
        global: {
          body: {
            bg: config.backgroundColor,
          },
        },
      },
      components: {
        Menu: {
          baseStyle: {
            list: {
              bg: config.backgroundColor,
            },
          },
        },
        Modal: {
          baseStyle: {
            dialog: {
              backgroundColor: config.backgroundColor,
            },
            header: {
              backgroundColor: config.backgroundColor,
            },
            body: {
              backgroundColor: config.backgroundColor,
            },
            footer: {
              px: 6,
              py: 4,
              backgroundColor: config.backgroundColor,
            },
          },
        },
      },
      semanticTokens: {
        colors: {
          background: {
            default: config.backgroundColor,
            _dark: config.backgroundColor,
          },
        },
      },
    };
  }

  // Overwrite config values based on publisher provided primary color
  if (config.primaryColor) {
    const colors = generateIntegratorColorScale(config.primaryColor);
    out = {
      ...out,
      colors: {
        integrator: colors,
      },
      shadows: {
        outline: `0 0 0 3px ${colors[600]}99`,
      },
      semanticTokens: {
        colors: {
          ...(out.semanticTokens?.colors || {}),
          lightBackground: {
            default: `${colors[50]}4D`,
            _dark: `${colors[50]}0D`,
          },
          lightBorder: {
            default: `${colors[200]}66`,
            _dark: `${colors[200]}1A`,
          },
        },
      },
    };
  }

  return out;
};

const theme = extendTheme(
  // In order to lessen the amount of boilerplate in our codebase, we set some default
  // prop values for Chakra components here, starting with the <Heading /> component.
  withDefaultSize({
    size: "md",
    components: ["Heading"],
  }),
  withDefaultSize({
    size: "lg",
    components: ["Radio"],
  }),

  withDefaultColorScheme({
    colorScheme: "integrator",
    components: ["Button", "Badge", "Radio", "Checkbox", "Link", "Tabs"],
  }),

  baseTheme
);

export default theme;
